import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles, Users, Repeat, RotateCw, Copy, Lock, Trophy, Check, X } from "lucide-react";
import { toast } from "sonner";
import { CelebrityCard } from "../components/CelebrityCard";
import { VersusBadge } from "../components/VersusBadge";
import { MashFactorBanner } from "../components/MashFactorBanner";
import { CELEBRITIES } from "../data/celebrities";
import { PRIME_REASONS, primeReasonLabel } from "../data/primeReasons";
import { mashFactorDisplay } from "../data/mashFactors";
import { getChallenge, completeChallenge } from "../lib/challengeApi";
import { StartButton } from "../components/StartButton";

const findC = (id) => CELEBRITIES.find((c) => c.id === id);

export default function PlayChallenge() {
  const nav = useNavigate();
  const { id } = useParams();
  const [challenge, setChallenge] = useState(null); // public view
  const [loadError, setLoadError] = useState(null);
  const [phase, setPhase] = useState("intro"); // intro -> tourney -> guess -> reveal
  const [matchupIdx, setMatchupIdx] = useState(0);
  const [defender, setDefender] = useState(null); // celebrity id currently defending
  const [selections, setSelections] = useState([]);
  const [reasonGuess, setReasonGuess] = useState(null);
  const [finalResult, setFinalResult] = useState(null); // server-revealed
  const [revealStep, setRevealStep] = useState(0); // 0 = winner, 1 = prime reveal, 2 = reason reveal
  const [submitting, setSubmitting] = useState(false);

  // Load challenge
  useEffect(() => {
    let alive = true;
    getChallenge(id)
      .then((c) => {
        if (!alive) return;
        setChallenge(c);
        if (c.challenge_status === "completed") {
          // Already completed — jump straight to reveal
          setFinalResult(c);
          setPhase("reveal");
          setRevealStep(2);
        } else {
          const list = c.challenger_ids || [];
          if (list.length >= 2) setDefender(list[0]);
        }
      })
      .catch((e) => setLoadError(e?.detail || "Challenge not found"));
    return () => { alive = false; };
  }, [id]);

  // Tournament format: survivor. defender vs next challenger, winner defends next.
  const nextChallengerIdx = matchupIdx + 1; // index into challenger_ids for the opponent

  const challenger = challenge && nextChallengerIdx < challenge.challenger_ids.length
    ? challenge.challenger_ids[nextChallengerIdx]
    : null;

  const totalMatchups = challenge ? challenge.challenger_ids.length - 1 : 0;

  const startTourney = () => setPhase("tourney");

  const pickWinner = (winnerId) => {
    if (!challenger) return;
    const nextSel = [
      ...selections,
      { matchupIndex: matchupIdx, a_id: defender, b_id: challenger, winner_id: winnerId },
    ];
    setSelections(nextSel);
    if (matchupIdx + 1 >= totalMatchups) {
      // Done — move to reason guess
      setPhase("guess");
    } else {
      setDefender(winnerId);
      setMatchupIdx(matchupIdx + 1);
    }
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    const last = selections[selections.length - 1];
    // The final winner is the last matchup's winner OR the defender if no matchups happened.
    const finalWinnerId = last ? last.winner_id : defender;
    try {
      const res = await completeChallenge(id, {
        receiver_selections: selections,
        final_winner_id: finalWinnerId,
        receiver_reason_guess: reasonGuess,
        receiver_factor_guess: challenge.mash_factor_id, // receiver already sees the factor; awarded automatically
      });
      setFinalResult(res);
      setPhase("reveal");
      setRevealStep(0);
      // Auto-progress reveal steps for drama
      setTimeout(() => setRevealStep(1), 1600);
      setTimeout(() => setRevealStep(2), 3400);
    } catch (e) {
      toast.error("Couldn't submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-16 pb-nav text-center">
          <h1 className="font-display text-4xl tracking-tight">Challenge not found</h1>
          <button onClick={() => nav("/home")} className="mt-6 rounded-full bg-[#FF107A] px-6 py-3 font-bold glow-pink">Home</button>
        </div>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-24 text-center">
          <div className="animate-pulse text-[#FDFBF7]/60">Loading challenge…</div>
        </div>
      </div>
    );
  }

  const senderName = challenge.sender_name || "Someone";
  const receiverName = challenge.receiver_name || "you";

  // ---------- INTRO ----------
  if (phase === "intro") {
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-6 pb-nav">
          <button onClick={() => nav("/home")} className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
            <ArrowLeft size={16} /> Home
          </button>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-3xl border border-[#FF107A]/30 bg-[#FF107A]/5 p-6">
            <div className="text-[11px] tracking-[0.35em] uppercase text-[#FF107A] font-semibold flex items-center gap-1">
              <Lock size={12} /> Hidden Prime Challenge
            </div>
            <h1 className="font-display text-4xl leading-[0.95] tracking-tight mt-2">
              <span className="text-[#FF107A]">{senderName}</span> thinks they know exactly who you&rsquo;ll choose.
            </h1>
            <p className="mt-3 text-sm text-[#FDFBF7]/85">
              Can they predict your ultimate Celebrity Smash winner?
            </p>
            {challenge.mash_factor_id && (
              <div className="mt-5">
                <MashFactorBanner
                  label={mashFactorDisplay(challenge.mash_factor_id, challenge.mash_factor_label)}
                  size="lg"
                  testId="hp-intro-mash-banner"
                />
              </div>
            )}
            <div className="mt-5 grid grid-cols-3 gap-2 text-center">
              <Stat label="Challengers" value={challenge.challenger_ids.length} />
              <Stat label="Rounds" value={totalMatchups} />
              <Stat label="Mode" value={challenge.challenge_mode?.charAt(0).toUpperCase() + challenge.challenge_mode?.slice(1)} />
            </div>
            <StartButton
              onClick={startTourney}
              testId="hp-start-btn"
              label="Start Smash"
            />
            <p className="mt-3 text-[10px] tracking-[0.25em] uppercase text-[#FDFBF7]/50 text-center">
              {senderName}&rsquo;s Prime + reason are locked on the server.
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  // ---------- TOURNEY ----------
  if (phase === "tourney") {
    const a = findC(defender);
    const b = findC(challenger);
    if (!a || !b) return null;
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-6 pb-nav">
          <div className="flex items-center justify-between">
            <button onClick={() => nav("/home")} className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
              <ArrowLeft size={16} /> Exit
            </button>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#00E5FF] font-bold">
              Round {matchupIdx + 1} of {totalMatchups}
            </div>
          </div>

          <div className="mt-3 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${totalMatchups}, minmax(0,1fr))` }}>
            {Array.from({ length: totalMatchups }).map((_, i) => (
              <div key={`hp-seg-${i}`} className={`h-1.5 rounded-full ${i < matchupIdx ? "bg-[#00E5FF]/70" : i === matchupIdx ? "seg-active" : "bg-white/10"}`} />
            ))}
          </div>

          {challenge.mash_factor_id && (
            <div className="mt-3">
              <MashFactorBanner
                label={mashFactorDisplay(challenge.mash_factor_id, challenge.mash_factor_label)}
                size="sm"
                testId="hp-tourney-mash-banner"
              />
            </div>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={matchupIdx}
              initial={{ opacity: 0, y: 12, filter: "blur(4px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -8, filter: "blur(4px)" }}
              transition={{ duration: 0.28 }}
              className="mt-6"
            >
              <h2 className="font-display text-4xl leading-[0.95] tracking-tight">
                {mashFactorDisplay(challenge.mash_factor_id, challenge.mash_factor_label)}
              </h2>
              <div className="relative mt-6 grid grid-cols-2 gap-3">
                <CelebrityCard celeb={a} onClick={() => pickWinner(a.id)} testId={`hp-pick-${a.id}`} />
                <CelebrityCard celeb={b} onClick={() => pickWinner(b.id)} testId={`hp-pick-${b.id}`} />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                  <VersusBadge small />
                </div>
              </div>
              {matchupIdx > 0 && (
                <p className="mt-3 text-center text-[10px] tracking-[0.25em] uppercase text-[#FDFBF7]/50">
                  {a.name} defended {matchupIdx} time{matchupIdx === 1 ? "" : "s"}
                </p>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
  }

  // ---------- REASON GUESS ----------
  if (phase === "guess") {
    const lastWinner = selections[selections.length - 1]?.winner_id;
    const winnerC = findC(lastWinner);
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-6 pb-nav">
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#00E5FF] font-bold">Your champion</div>
          <h1 className="font-display text-5xl leading-[0.9] tracking-tight mt-2">
            <span className="text-[#FF107A]">{winnerC?.name}</span>
          </h1>
          <p className="mt-3 text-sm text-[#FDFBF7]/70">
            Before we reveal what {senderName} predicted…
          </p>
          <div className="mt-6 rounded-2xl border border-[#A855F7]/30 bg-[#A855F7]/5 p-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A855F7] font-bold">Your guess</div>
            <p className="mt-1 text-sm text-[#FDFBF7]/90">
              Why do you think <span className="font-bold">{senderName}</span> thought you&rsquo;d pick your ultimate winner?
            </p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="hp-guess-picker">
              {PRIME_REASONS.filter((r) => r.id !== "custom").map((r) => (
                <button
                  key={r.id}
                  data-testid={`hp-guess-${r.id}`}
                  onClick={() => setReasonGuess(r.id)}
                  className={`rounded-full px-4 py-2 text-sm font-medium border transition-all ${
                    reasonGuess === r.id
                      ? "bg-[#A855F7] text-[#FDFBF7] border-[#A855F7]"
                      : "bg-white/5 border-white/15 hover:border-[#A855F7]"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <button
            data-testid="hp-submit-btn"
            disabled={!reasonGuess || submitting}
            onClick={submit}
            className={`mt-6 w-full rounded-full py-3 font-bold flex items-center justify-center gap-2 transition-all ${
              reasonGuess && !submitting ? "bg-[#FF107A] text-[#FDFBF7] glow-pink" : "bg-white/10 text-[#FDFBF7]/40 cursor-not-allowed"
            }`}
          >
            {submitting ? "Submitting…" : "Reveal Results"} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  // ---------- REVEAL ----------
  if (phase === "reveal" && finalResult) {
    return <RevealScreen result={finalResult} senderName={senderName} onHome={() => nav("/home")} onNewChallenge={() => nav("/challenge/new")} step={revealStep} setStep={setRevealStep} />;
  }

  return null;
}

const Stat = ({ label, value }) => (
  <div className="rounded-xl bg-black/30 border border-white/10 py-3">
    <div className="font-display text-3xl">{value}</div>
    <div className="text-[9px] tracking-[0.2em] uppercase text-[#FDFBF7]/60 mt-0.5">{label}</div>
  </div>
);

const RevealScreen = ({ result, senderName, onHome, onNewChallenge, step, setStep }) => {
  const winnerC = findC(result.final_winner_id);
  const primeC = findC(result.hidden_prime_id);
  const primeMatch = result.prime_match;
  const reasonMatch = result.reason_match;
  const perfect = result.perfect_smash;

  const senderReasonLabel = result.prime_reason === "custom"
    ? (result.prime_reason_custom || "a custom reason")
    : primeReasonLabel(result.prime_reason);

  const copyShareText = async (includeCustom = false) => {
    const site = typeof window !== "undefined" ? window.location.origin : "https://celebritysmash.app";
    let text;
    if (perfect) {
      text = `${senderName} predicted my Celebrity Smash winner AND knew exactly why I'd choose them. PERFECT SMASH. Play at ${site}`;
    } else if (primeMatch) {
      text = `${senderName} predicted my Celebrity Smash winner—and they nailed it. Play at ${site}`;
    } else {
      text = `${senderName} thought they knew my Celebrity Smash winner. They were wrong. Play at ${site}`;
    }
    if (includeCustom && result.prime_reason === "custom" && result.prime_reason_custom) {
      text += `\nTheir reason: "${result.prime_reason_custom}"`;
    }
    await navigator.clipboard.writeText(text);
    toast.success("Share text copied.");
  };

  return (
    <div className="min-h-screen spotlight grain scanlines relative overflow-hidden">
      <div className="relative z-10 max-w-md mx-auto px-5 pt-6 pb-nav">
        <button onClick={onHome} className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
          <ArrowLeft size={16} /> Home
        </button>

        {/* Step 0: Winner reveal */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-6"
        >
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#00E5FF] font-bold">Your Celebrity Smash Champion</div>
          <h1 className="font-display text-6xl sm:text-7xl leading-[0.85] tracking-tight mt-2 slam-in" data-testid="hp-final-winner">
            <span className="text-[#FF107A]">{winnerC?.name}</span>
          </h1>
          {result.mash_factor_id && (
            <div className="mt-4">
              <MashFactorBanner
                label={mashFactorDisplay(result.mash_factor_id, result.mash_factor_label)}
                size="sm"
                testId="hp-reveal-mash-banner"
              />
            </div>
          )}
        </motion.div>

        {/* Step 1: Prime reveal */}
        {step >= 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 180, damping: 20 }}
            className="mt-8 rounded-2xl border border-white/10 bg-[#16141D]/90 p-5"
            data-testid="hp-prime-reveal"
          >
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#A855F7] font-bold">{senderName} predicted</div>
            <div className="mt-1 font-display text-4xl leading-none tracking-tight">
              {primeC?.name}
            </div>
            <div className={`mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase font-bold px-3 py-1.5 rounded-full ${
              primeMatch ? "bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/50" : "bg-[#FF107A]/20 text-[#FF107A] border border-[#FF107A]/50"
            }`}>
              {primeMatch ? <><Check size={12} /> Correct · Prime Match</> : <><X size={12} /> Wrong · Prime Missed</>}
            </div>
          </motion.div>
        )}

        {/* Step 2: Reason reveal + score */}
        {step >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 180, damping: 20, delay: 0.1 }}
            className="mt-5 rounded-2xl border border-white/10 bg-[#16141D]/90 p-5"
            data-testid="hp-reason-reveal"
          >
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#00E5FF] font-bold">
              {senderName} thought you&rsquo;d choose because of
            </div>
            <div className="mt-1 font-display text-3xl tracking-tight break-words">{senderReasonLabel}</div>
            <div className="mt-2 text-[11px] text-[#FDFBF7]/60">
              You guessed: <span className="text-[#FDFBF7] font-semibold">{primeReasonLabel(result.receiver_reason_guess)}</span>
            </div>
            <div className={`mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase font-bold px-3 py-1.5 rounded-full ${
              reasonMatch ? "bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/50" : "bg-white/10 text-[#FDFBF7]/60 border border-white/20"
            }`}>
              {reasonMatch ? <><Check size={12} /> Reason Match</> : <><X size={12} /> Different reason</>}
            </div>
          </motion.div>
        )}

        {/* Score card */}
        {step >= 2 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, type: "spring", stiffness: 160 }}
            data-testid="hp-score-card"
            className={`mt-6 rounded-3xl p-6 text-center border ${
              perfect ? "border-[#FFD700]/60" : primeMatch ? "border-[#00E5FF]/60" : "border-white/15"
            }`}
            style={{
              background: perfect
                ? "radial-gradient(circle at 30% 20%, rgba(255,215,0,0.25), transparent 60%), #16141D"
                : primeMatch
                ? "radial-gradient(circle at 30% 20%, rgba(0,229,255,0.25), transparent 60%), #16141D"
                : "#16141D",
            }}
          >
            <div className={`font-display text-2xl tracking-[0.15em] ${perfect ? "text-[#FFD700]" : primeMatch ? "text-[#00E5FF]" : "text-[#FDFBF7]/70"}`}>
              {perfect ? "PERFECT SMASH!" : primeMatch ? "PRIME MATCH!" : reasonMatch ? "REASON MATCH!" : "No Match"}
            </div>
            {result.classification && (
              <div
                data-testid="hp-classification"
                className={`mt-3 inline-block font-display text-3xl leading-none tracking-tight px-3 py-1 rounded-xl border-2 ${
                  result.classification === "Oracle" ? "border-[#FFD700] text-[#FFD700]" :
                  result.classification === "Savant" ? "border-[#FF107A] text-[#FF107A]" :
                  result.classification === "Bestie" ? "border-[#00E5FF] text-[#00E5FF]" :
                  "border-white/25 text-[#FDFBF7]/80"
                }`}
              >
                {result.classification.toUpperCase()}
              </div>
            )}
            {result.classification_label && (
              <div className="mt-2 text-[11px] tracking-[0.25em] uppercase italic text-[#FDFBF7]/80" data-testid="hp-classification-label">
                &ldquo;{result.classification_label}&rdquo;
              </div>
            )}
            <div className="font-display text-8xl leading-none tracking-tight mt-3 tabular"
              style={{
                color: perfect ? "#FFD700" : primeMatch ? "#00E5FF" : reasonMatch ? "#A855F7" : "#FDFBF7",
                textShadow: perfect
                  ? "0 0 24px rgba(255,215,0,0.5)"
                  : primeMatch
                  ? "0 0 24px rgba(0,229,255,0.5)"
                  : "none",
              }}
            >
              {result.total_score}
            </div>
            <div className="text-xs tracking-[0.25em] uppercase text-[#FDFBF7]/60 mt-1">points</div>
          </motion.div>
        )}

        {/* Post-game CTAs */}
        {step >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mt-6 space-y-2"
          >
            <button
              data-testid="hp-revenge-btn"
              onClick={onNewChallenge}
              className="w-full rounded-full bg-[#FF107A] py-3 font-bold text-[#FDFBF7] glow-pink flex items-center justify-center gap-2"
            >
              <Repeat size={16} /> Revenge Smash
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="hp-challenge-friend-btn"
                onClick={onNewChallenge}
                className="rounded-full border-2 border-[#A855F7] text-[#FDFBF7] py-3 font-bold flex items-center justify-center gap-2 hover:bg-[#A855F7]/10"
              >
                <Users size={16} /> Challenge Friend
              </button>
              <button
                data-testid="hp-play-again-btn"
                onClick={onNewChallenge}
                className="rounded-full border-2 border-white/15 py-3 font-bold flex items-center justify-center gap-2 hover:border-white/30"
              >
                <RotateCw size={16} /> Play Again
              </button>
            </div>
            <button
              data-testid="hp-copy-share-btn"
              onClick={() => copyShareText(false)}
              className="w-full rounded-full border-2 border-[#00E5FF] text-[#00E5FF] py-3 font-bold flex items-center justify-center gap-2 hover:bg-[#00E5FF]/10 mt-2"
            >
              <Copy size={16} /> Copy share text
            </button>
            {result.prime_reason === "custom" && result.prime_reason_custom && (
              <p className="mt-3 text-center text-[10px] tracking-[0.25em] uppercase text-[#FDFBF7]/40">
                Your friend&rsquo;s custom reason stays private unless you copy the extended version.
              </p>
            )}
          </motion.div>
        )}

        {/* Skip button while reveal auto-plays */}
        {step < 2 && (
          <button
            onClick={() => setStep(2)}
            className="mt-6 w-full text-center text-xs tracking-[0.25em] uppercase text-[#FDFBF7]/50 hover:text-[#FDFBF7]"
          >
            Skip reveal →
          </button>
        )}
      </div>
    </div>
  );
};
